import { lazy, Suspense, useState } from "react";
import { Link } from "react-router-dom";
import { LayoutGrid, Map as MapIcon, Globe } from "lucide-react";
import { RealtyFilters, applyFilters, EMPTY_FILTERS } from "@/components/site/RealtyFilters";
import { ShareButtons } from "@/components/site/ShareButtons";
import { InternationalShowcase } from "@/components/site/InternationalShowcase";
import { CurrencySwitcher } from "@/components/site/CurrencySwitcher";
import { ListingAlerts } from "@/components/site/ListingAlerts";
import { useCurrency, parseMoney, CURRENCY_SYMBOL } from "@/context/CurrencyContext";
import { IMAGES } from "@/lib/api";
import { ArmBar, ArmIntroMark } from "@/components/site/Chrome";
import { ContactSection } from "@/components/site/Contact";
import { PropertyCard } from "@/components/site/Cards";
import { useCollection, usePageMeta } from "@/hooks/useData";

const FILTERS = ["All", "For Sale", "For Lease", "Investment"];
const PropertyMap = lazy(() => import("@/components/site/PropertyMap").then((m) => ({ default: m.PropertyMap })));

export default function Realty() {
  usePageMeta("Property for Sale & Lease in Nigeria | Wolfe Realtors",
    "Wolfe Realtors: brokerage, buying, sales, leasing, investment advisory and management. Browse our latest property offerings in Nigeria and abroad.",
    "https://www.greywolfeng.com/api/share/page/realty/image.jpg");
  const items = useCollection("properties");
  const [filter, setFilter] = useState("All");
  const [view, setView] = useState("grid");
  const [market, setMarket] = useState("all");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const { currency, rates } = useCurrency();
  const filterCur = currency === "original" ? "NGN" : currency;
  // Price (any listed currency) -> number in the filter currency.
  const valueIn = (cur) => (price) => {
    const p = parseMoney(price);
    if (!p) return null;
    if (p.cur === cur) return p.value;
    return rates ? (p.value / rates[p.cur]) * rates[cur] : null;
  };
  const byMarket = (items || []).filter((p) => market === "all" || (market === "international" ? p.international : !p.international));
  const list = applyFilters(byMarket.filter((p) => filter === "All" || p.listing_type === filter), filters, valueIn(filterCur));
  const intl = (items || []).filter((p) => p.international);
  const showIntl = () => {
    setMarket("international");
    setFilters(EMPTY_FILTERS);
    document.getElementById("properties")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="page-in">
      <section className="bg-gw-paper text-gw-ink py-24 md:py-32 border-t-8 border-gw-rule" data-testid="realty-page">
        <div className="max-w-6xl mx-auto px-6">
          <ArmBar current="realty" light />
          <ArmIntroMark arm="realty" />
          <div className="grid md:grid-cols-2 gap-12 items-center mt-10">
            <div className="min-w-0">
              <h1 className="lux gw-h1">
                <span className="ln"><span>The truth,</span></span>
                <span className="ln"><span>then the deal.</span></span>
              </h1>
              <p className="mt-6 gw-body" data-testid="realty-differentiator">Honest advice: we tell investors the truth about a deal.</p>
              <p className="lab mt-6 text-gw-accent" data-testid="realty-services">Brokerage / Buying / Sales / Leasing / Investment advisory &amp; management</p>
              <div className="flex flex-wrap gap-4 mt-10">
                <a href="#properties" data-testid="realty-browse-link" className="group inline-flex gap-3 lab bg-gw-btn text-gw-btnfg px-6 py-4 transition-colors duration-200 hover:bg-gw-btnhover">
                  Browse properties <span className="transition-transform duration-200 group-hover:translate-y-0.5">↓</span>
                </a>
                <Link to="/shortlets" data-testid="realty-shortlets-link" className="group inline-flex gap-3 lab border border-gw-rule/50 px-6 py-4 transition-colors duration-200 hover:bg-gw-hover">
                  Book a shortlet <span className="transition-transform duration-200 group-hover:translate-x-1">→</span>
                </Link>
              </div>
              <ShareButtons kind="page" id="realty" text="Wolfe Realtors — property sales, leasing & investment advisory" className="mt-8" testPrefix="realty-share" />
            </div>
            <figure className="aspect-[3/4] bg-gw-soft overflow-hidden m-0">
              <img src={IMAGES.realty} alt="Keys handed over to a new owner" className="w-full h-full object-cover" />
            </figure>
          </div>
        </div>
      </section>

      <InternationalShowcase items={intl} onSeeAll={showIntl} />

      <section id="properties" className="bg-gw-paper2 text-gw-ink py-24 md:py-32 scroll-mt-16" data-testid="properties-section">
        <div className="max-w-6xl mx-auto px-6">
          <p className="lab text-gw-accent">Wolfe Realtors</p>
          <div className="flex flex-wrap gap-2 mt-4" role="tablist" data-testid="market-tabs">
            {[["all", "All markets"], ["local", "Local"], ["international", "International"]].map(([v, label]) => (
              <button key={v} type="button" role="tab" aria-selected={market === v} onClick={() => setMarket(v)} data-testid={`market-tab-${v}`}
                className={`lab px-5 py-3 border-b-2 transition-colors duration-200 inline-flex items-center gap-2 ${market === v ? "border-gw-rule text-gw-ink" : "border-transparent opacity-60 hover:opacity-100"}`}>
                {v === "international" && <Globe size={13} />}{label}
                {v === "international" && intl.length > 0 && <span className="text-[10px] bg-gw-btn text-gw-btnfg px-1.5">{intl.length}</span>}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-end justify-between gap-6 mt-6 mb-8">
            <h2 className="lux gw-h2">Latest properties</h2>
            <div className="flex flex-wrap gap-2 items-center" role="tablist">
              <div className="flex border border-gw-rule/30 mr-2" data-testid="properties-view-toggle">
                {[["grid", "Grid", LayoutGrid], ["map", "Map", MapIcon]].map(([v, label, Icon]) => (
                  <button key={v} type="button" onClick={() => setView(v)} data-testid={`properties-view-${v}`} aria-pressed={view === v}
                    className={`lab px-4 py-2 inline-flex items-center gap-2 transition-colors duration-200 ${view === v ? "bg-gw-btn text-gw-btnfg" : "hover:bg-gw-hover"}`}>
                    <Icon size={13} /> {label}
                  </button>
                ))}
              </div>
              {FILTERS.map((f) => (
                <button key={f} type="button" onClick={() => setFilter(f)} data-testid={`properties-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`lab px-4 py-2 border transition-colors duration-200 ${filter === f ? "bg-gw-btn text-gw-btnfg border-gw-btn" : "border-gw-rule/30 hover:bg-gw-hover"}`}>
                  {f}
                </button>
              ))}
            </div>
          </div>
          {items !== null && (
            <>
              <div className="flex justify-end mb-6"><CurrencySwitcher testPrefix="listings-currency" /></div>
              <RealtyFilters items={byMarket} value={filters} onChange={setFilters} symbol={CURRENCY_SYMBOL[filterCur]} />
            </>
          )}
          {items === null ? (
            <div className="grid md:grid-cols-4 gap-6">{[0, 1, 2].map((i) => <div key={i} className="md:col-span-2 aspect-[2/1] bg-gw-card animate-pulse" />)}</div>
          ) : list.length === 0 ? (
            <p className="gw-body text-gw-accent/70" data-testid="properties-empty">No properties in this category right now — send us an enquiry and we'll tell you what's coming.</p>
          ) : view === "map" ? (
            <Suspense fallback={<div className="h-[560px] bg-gw-card animate-pulse" />}>
              <PropertyMap items={list} />
            </Suspense>
          ) : (
            <div className="grid md:grid-cols-4 gap-6" data-testid="properties-grid">
              {list.map((p, i) => <PropertyCard key={p.id} p={p} large={i === 0 && list.length > 1} idx={i} />)}
            </div>
          )}
        </div>
      </section>
      <ListingAlerts />
      <ContactSection defaultCompany="Wolfe Realtors" />
    </div>
  );
}
